import {ReferenceType} from "@luciad/ria/reference/ReferenceType";
import {Map} from "@luciad/ria/view/Map";
import {WebGLMap} from "@luciad/ria/view/WebGLMap";
import React, {PropsWithChildren, useEffect, useRef, useState} from "react";
import {MapContext} from "../hooks/useMapContext";
import {useMapURLParams} from "../hooks/useMapURLParams";
import {createFusionBackgroundLayer, createFusionElevationLayer} from "../LayerFactory";
import "../typings/image-typings"; //fixes typings of image import in all depending samples
import {isUndefined} from "../util/Lang";

interface Props {
  /**
   * Called when the map has been initialized.
   * Use this to add layers etc.
   */
  onInit?: (map: Map) => void;
  /**
   * Include a default background imagery layer.
   */
  includeBackground?: boolean;
  /**
   * Include a default elevation layer.
   */
  includeElevation?: boolean;
  /**
   * Called when the map has been destroyed.
   * Use this to cleanup listeners etc.
   */
  onDestroy?: (map: Map) => void;
  /**
   * If specified, the map's reference will always be set to the specified value.
   * If not specified, the map's reference will be a reference specified in the URL,
   * e.g. http://localhost:3000/?webgl=true&reference=EPSG:3857 for a Web Mercator map.
   * If the URL also doesn't specify a reference, <code>defaultReference</code> will be used.
   */
  reference?: string;
  /**
   * The map's default reference if no reference is specified in the URL (cf. the <code>reference</code> prop).
   */
  defaultReference?: string;
  /**
   * Indicates whether a WebGLMap is created.
   */
  webgl?: boolean;
  /**
   * React children.
   */
  children?: React.ReactNode;
  /**
   * A class name to apply to the map (<code>map.domNode</code>).
   */
  className?: string;
}

export const SampleMap = ({
                            reference,
                            defaultReference = "EPSG:32662",
                            webgl,
                            onInit,
                            includeBackground = true,
                            includeElevation = true,
                            onDestroy,
                            children,
                            className = "map"
                          }: PropsWithChildren<Props>) => {
  const mapDomNode = useRef<HTMLDivElement | null>(null);
  const [map, setMap] = useState<Map | null>(null);

  const [webglFromUrl, referenceFromUrl] = useMapURLParams();

  useEffect(() => {
    let newMap: Map;
    if (mapDomNode.current) {
      const useWebGLMap = isUndefined(webgl) ? (webglFromUrl !== "false") : webgl;
      const ref = reference ?? referenceFromUrl ?? defaultReference;
      if (useWebGLMap) {
        newMap = new WebGLMap(mapDomNode.current, {reference: ref});
      } else {
        newMap = new Map(mapDomNode.current, {reference: ref});
      }

      const is3DMap = newMap.reference.referenceType === ReferenceType.GEOCENTRIC;

      if (includeElevation && is3DMap) {
        createFusionElevationLayer().then(layer => {
          newMap.layerTree.addChild(layer);
        });
      }

      if (includeBackground) {
        createFusionBackgroundLayer().then(layer => {
          newMap.layerTree.addChild(layer, "bottom");
        });
      }
      if (onInit) {
        onInit(newMap);
      }
      setMap(newMap);
    }

    return (): void => {
      setMap(null);
      if (onDestroy) {
        onDestroy(newMap);
      }
      newMap?.destroy();
    }
  }, [reference, webgl]);

  let mapOverlayNodes = (<></>);
  if (map) {
    mapOverlayNodes = (
        <MapContext.Provider value={{map}}>
          {children}
        </MapContext.Provider>
    );
  }

  return (
      <div ref={mapDomNode} className={className}>
        {mapOverlayNodes}
      </div>
  );
}